import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Cloud, TrendingUp, Briefcase } from 'lucide-react';
import { FadeInUp, StaggerContainer, StaggerItem } from './animations/AnimatedSection';
import { useTranslation } from 'react-i18next';

const iconMap = {
  Brain: Brain,
  Cloud: Cloud,
  TrendingUp: TrendingUp,
  Briefcase: Briefcase,
};

const Services = () => {
  const { t } = useTranslation();

  const services = [
    { id: 1, icon: 'Brain', titleKey: 'services.service1.title', descKey: 'services.service1.description' },
    { id: 2, icon: 'Cloud', titleKey: 'services.service2.title', descKey: 'services.service2.description' },
    { id: 3, icon: 'TrendingUp', titleKey: 'services.service3.title', descKey: 'services.service3.description' },
    { id: 4, icon: 'Briefcase', titleKey: 'services.service4.title', descKey: 'services.service4.description' },
  ];

  return (
    <section id="services" className="py-32 bg-white relative overflow-hidden">
      {/* Animated background accent */}
      <motion.div
        className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-stone-50 to-transparent"
        initial={{ opacity: 0, x: 100 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <FadeInUp className="max-w-2xl mb-20">
          <motion.span
            className="text-xs font-medium text-gold tracking-[0.3em] uppercase mb-4 block"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {t('services.label')}
          </motion.span>
          <motion.h2
            className="font-serif text-4xl md:text-5xl text-navy font-light leading-tight mb-6 overflow-hidden"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <motion.span
              className="block"
              initial={{ y: 80 }}
              whileInView={{ y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {t('services.title1')}
            </motion.span>
            <motion.span
              className="block"
              initial={{ y: 80 }}
              whileInView={{ y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {t('services.title2')}
            </motion.span>
          </motion.h2>
          <motion.p
            className="text-navy/60 font-light text-lg leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {t('services.description')}
          </motion.p>
        </FadeInUp>

        {/* Services Grid */}
        <StaggerContainer staggerDelay={0.15} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <StaggerItem key={service.id}>
                <motion.div
                  className="group relative p-8 bg-white border border-gold/20 rounded-sm h-full"
                  whileHover={{ 
                    y: -10, 
                    boxShadow: '0 25px 50px -12px rgba(201, 169, 98, 0.15)',
                    borderColor: 'rgba(201, 169, 98, 0.4)'
                  }}
                  transition={{ duration: 0.4 }}
                >
                  {/* Glow effect on hover */}
                  <motion.div
                    className="absolute -inset-px bg-gradient-to-r from-gold/10 via-transparent to-gold/10 rounded-sm opacity-0 group-hover:opacity-100"
                    transition={{ duration: 0.4 }}
                  />

                  {/* Icon Container */}
                  <motion.div
                    className="relative w-16 h-16 flex items-center justify-center mb-8 rounded-sm bg-stone-50 group-hover:bg-gold/10 transition-colors duration-500"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <Icon className="w-7 h-7 text-gold" />
                  </motion.div>

                  {/* Content */}
                  <h3 className="relative font-serif text-2xl text-navy font-light mb-4">
                    {t(service.titleKey)}
                  </h3>
                  <p className="relative text-navy/60 font-light leading-relaxed">
                    {t(service.descKey)}
                  </p>

                  {/* Animated bottom line */}
                  <motion.div
                    className="absolute bottom-0 left-0 h-px bg-gradient-to-r from-gold via-gold-dark to-transparent"
                    initial={{ width: 0 }}
                    whileHover={{ width: '100%' }}
                    transition={{ duration: 0.6 }}
                  />
                </motion.div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </div>
    </section>
  );
};

export default Services;
